
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { VALUE_ENTER, VALUE_ESCAPE, WIZ_EVENT_TYPE, WizMathUtils } from '@wizeoo/akm-core-lib';
import {  WizBasePopUp, WizIcon, WizTextInput, WizTranslationBasic, WizValidationContainerDirective, WizValidationError, WizValidationErrors, WizValidatorFn, WizVgroup, WizEventManager, WizEvent } from '@wizeoo/akm-component-lib';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';

import { ScenarioVO } from '../../../vo/scenarii/scenario.vo';
import { Model } from '../../../model/model';
import { StyleEngine } from '../../../manager/workingdata/style.engine';
import { ViewData } from '../../../model/view.data';
import { ColorPickerPop } from '../../colorpicker/color-picker.pop';

/** params de la popUp */
export interface addScenarioParam {
  model: Model,
  scenarii: ScenarioVO[],
  parent: ScenarioVO,
  callback: Function,
  mode: string, // type d'action (add / edit) 
}

/**
 * popUp d'ajout de scenario par l'utilisateur
 */
@Component({
  selector: 'scenadd-pop',
  templateUrl: './scenadd-pop.html',
  styles: [':host {max-width: 100%}'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenAddPop extends WizBasePopUp implements OnDestroy {

  constructor(public elementRef: ElementRef, protected cdref: ChangeDetectorRef) {
    super();
  }

  // validation sur enter, sortie sur Esc
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === VALUE_ENTER && !this.hasError) {
      this.save();
    }
    if (event.key === VALUE_ESCAPE) {
      this.hide();
    }
  }

  /** Text input de saisie du code */
  @ViewChild('tiCode', { read: WizTextInput }) tiCode: WizTextInput;
  /** Composant de saisie du libellé traductible */
  @ViewChild('trsLabel', { read: WizTranslationBasic }) trsLabel: WizTranslationBasic;
  /** Composant de saisie de la description */
  @ViewChild('trsDesc', { read: WizTranslationBasic }) trsDesc: WizTranslationBasic;
  /** Button d'ajout de scenario sur 1er step */
  @ViewChild('btnAddScen', { read: WizIcon }) btnAddScen: WizIcon;
  /** couleur des barres de menus */
  @ViewChild('blockBack') blockBack: ElementRef; // zone de dessin de la couleur de background
  /** Zone de validation des champs de saisie de l'élément */
  @ViewChild('vgAddValidator', { read: WizVgroup, static: false }) vgAdd: WizVgroup;
  @ViewChild('vgAddValidator', { read: WizValidationContainerDirective, static: false, }) private vgAddValidator: WizValidationContainerDirective;


  public scenarii: ScenarioVO[] = null; // les scenarii de cette vision (les vrais, pas des clones)
  public clones: ScenarioVO[] = null; // la collection de clones de travail
  public parent: ScenarioVO = null; // le parent
  public clone: ScenarioVO = null; // le clone de travail
  public backgroundColor = StyleEngine.STYLE_DEFAULT_SCEN_COLOR; // --grid-header-background-color
  public css: any = null;
  public doSave: boolean = false;

  public get model(): Model {
    return this.params.model;
  }

  public get params(): addScenarioParam {
    return this.datas;
  }

  public get hasParent(): boolean{
    return !!this.parent;
  }


  public show(): void {
    this.feed(); // alimentation des steps (1x)
    this.setListeners();
  }

  private feed(): void {


    this.scenarii = this.params.scenarii;
    this.clones = [... this.model.scenarioEngine.cloneAll(this.scenarii)]; // la collection de clone de travail

    if(this.params.mode === EM_ACTION.CREATE) {
      this.parent = this.params.parent;
      this.clone =  this.model.scenarioEngine.createChildScen( this.model.currentVision, this.params.parent, this.scenarii);
      this.clone.userMode = true;
      this.clone.owner =  this.model.connectedUser.mail;
      this.clones.push(this.clone); // ajout à la collection de travail
       this.model.scenarioEngine.initParameters(this.clone,  this.model.currentVision); // on s'assure que la liste des steps est complète  
    } else {
      this.clones.forEach(sc => {
        if(sc.code === this.params.parent.code) {
          this.clone = sc;
        }
      });
      
      //on cherche son parent
      this.parent =  this.model.scenarioEngine.getScenario(this.clone.visionCode, this.clone.parentCode);

    }


    // alimentation du code
    this.tiCode.wizValue = this.clone.code;
    this.tiCode.wizRestrict = ViewData.CODE_REGEXP; // restriction sur le code des scenarii
    this.tiCode.wizDisabled = this.clone.exist;

    // alimentation du libellé
    this.trsLabel.translationData =  this.model.localeEngine.buildTranslationData(this.clone.labelVO);
    this.trsDesc.translationData =  this.model.localeEngine.buildTranslationData(this.clone.descriptionVO);
    
    // couleur de la marque de scenario
    if (!!this.clone.color) {
      this.backgroundColor = this.clone.color;
    } else {
      this.backgroundColor = StyleEngine.STYLE_DEFAULT_SCEN_COLOR;
    }
    // construction du CSS actif
    this.css = StyleEngine.STYLE_BACKGROUND_COLOR + ViewData.KEY_PAIR_SEPARATOR + this.backgroundColor;
  }

  /** pose des écouteurs  */
  private setListeners(): void {
    // écouteur global change pour modelUpdate
    WizEventManager.addListener(this, this.vgAdd, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateModel);

  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, this.vgAdd, WIZ_EVENT_TYPE.MODEL_UPDATE);
  }

  /**
   * Modification du model dans la zone de saisie
   * @param e Evenement model change
   */
  private updateModel(e: WizEvent): void {
    switch (e.component) {
      case this.tiCode:
        this.clone.code = this.tiCode.wizValue?.toUpperCase();
        break;
      case this.trsLabel:
        this.clone.labelVO.updateTranslationEntry(e.value);
        this.clone.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
        break; 
      case this.trsDesc:
        this.clone.descriptionVO.updateTranslationEntry(e.value);
        this.clone.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
        break;
    }
    this.checkErrors();
  }


  /** sur save, alimentation de l'objet de configuration */
  public save(): void { 
    let errors = this.vgAddValidator.check(true, true);

    if (errors?.length > 0) {
      // WizPopUp.toast('$.wg.error.$', WizIcon.TOAST_ERROR).show();
    } else {
       this.model.scenarioEngine.merge(this.clones, this.scenarii, this.backFromMerge.bind(this));
    }
    // console.log('errors on save', errors);
  }


  private backFromMerge(vos: ScenarioVO[], status: string): void {
    if(this.params.callback) {
      this.params.callback(vos, status);
    }
    this.hide();
  } 

  /** libellé du scénario parent */
  public get parentColor(): string {
    let result: string = '';
    result = this.parent?.color || this.backgroundColor;;
    return result;
  }

  /** libellé du scénario parent */
  public get parentLabel(): string {
    let result: string = '';
    result = this.parent?.trsLabel || '';
    return result;
  }

  public hasError: boolean = false;
  
  public checkErrors(): void {
    let result = false;
    let errors = this.vgAddValidator.check(true, true);
    if (errors?.length > 0) {
      result = true;
    }
    this.hasError = result;
  }

   /**
   * Validateur d'unicité du code de la vision
   */
  public uniqueCodeValidator(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      
      if (this.tiCode.wizValue?.length > 0) {
        if (this.tiCode.wizValue.toUpperCase() === ViewData.wildcardMainScenario // pas MAIN
        ) {
          const temp: WizValidationError = {
            wizError: '$.common.keyword.$',
            wizErrorParams: [],
          };
          return [temp];
        }
        
        // Recherche d'une autre avec le même code
        let other = new Map(this.scenarii?.filter(item => item.uid !== this.clone.uid).map(item => [item.code, item])).get(this.tiCode.wizValue.toUpperCase());

        // Si la vision existe deja avec le meme code on affiche le message
        if (other) {
          const temp: WizValidationError = {
            wizError: '$.scen.error.code.unique.$',
            wizErrorParams: [],
          };
          return [temp];
        }
      }
      return null;
    };
  } 

 /* ##############################################################################
                   gestion du colorPicker pour la conf du header
     ############################################################################## */

     /**
      * affichage de la popUp du colorPicker
      * @param pEvent click
      */
     public showColorPicker(pEvent: Event): void {
       pEvent.stopPropagation();
       pEvent.preventDefault();
       ColorPickerPop.show(this.blockBack.nativeElement, pEvent.target, this.backgroundColor, this.backFromPicker.bind(this));
     }
   
     /**
      * retour de colorPicker, alimentation des couleurs du header
      *
      * @param target le span source pour font ou background
      * @param color la couleur du picker
      */
     public backFromPicker(target: any, color: string): void {
       this.backgroundColor = color;
       this.feedHeaderStyle();
     }
   
     /** alimentation des slots de couleurs pour le header */
     public feedHeaderStyle(): void {
       this.css = StyleEngine.STYLE_BACKGROUND_COLOR + ViewData.KEY_PAIR_SEPARATOR + this.backgroundColor;
       this.clone.color = this.backgroundColor;
       // WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
       this.cdref.markForCheck();
     }
   
     /** click sur les icones de suppression de style de header */
     public resetColor(who: string): void {
       this.backgroundColor = StyleEngine.STYLE_DEFAULT_BAR_COLOR; // --grid-header-background-color
       this.feedHeaderStyle();
     }

  /** desactivation des boutons save et cancel sur updateModel */
  public get disableSave(): boolean {
    let result: boolean = false;
    result = this.hasError;
    return result;
  }


}
